// src/components/Button.tsx
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { LinkProps } from 'react-router-dom';

// Base props for the Button component
type ButtonProps = {
  variant?: 'primary' | 'secondary';
};

// Define a polymorphic type that adjusts props based on the 'as' prop
type PolymorphicButton<C extends React.ElementType> = {
  as?: C;
} & (C extends typeof Link
  ? Omit<LinkProps, 'as'> & ButtonProps // When as is Link, require LinkProps
  : React.ComponentPropsWithRef<C> & ButtonProps); // For other elements, use their props

// Create the styled Link component
const StyledButton = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['variant'].includes(prop),
})<ButtonProps>`
  padding: 10px 20px;
  background-color: ${({ theme, variant }) =>
    variant === 'secondary' ? theme.colors.muted : theme.colors.primary};
  color: white;
  text-decoration: none;
  border-radius: 5px;
  font-size: 1rem;
  transition: background-color 0.3s;
  width: 100%;
  text-align: center;
  cursor:pointer;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: auto;
    padding: 12px 24px;
  }

  &:hover {
    background-color: ${({ theme, variant }) =>
      variant === 'secondary' ? theme.colors.text : theme.colors.primaryHover};
  }
`;

// Define the Button component with proper polymorphic typing
const Button = <C extends React.ElementType = typeof Link>(
  props: PolymorphicButton<C> & { children?: React.ReactNode }
) => {
  const { as: Component = Link, ...rest } = props;
  return <StyledButton as={Component as any} {...rest} />;
};

export { Button };